import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, viewChild, WritableSignal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DonationService } from '../../donation/donation.service';
import { DonationBillingAddress, DonationCheckoutRequest, DonationErrorCode, DonationOpaqueData } from '../../donation/donation.types';
import { LocalStorageService } from '../../local-storage.service';
import { LOCALSTORAGE_KEYS } from '../../constants/databases';
import { Session } from '../../types/session.type';
import { AuthorizeService } from '../../purchase/authorize/authorize.service';
import { ANetPayload, ANetResponse } from '../../purchase/authorize/authorize.types';
import { TurnstileComponent } from '../../turnstile/turnstile.component';

/**
 * The donation checkout of DEV-10862 / ELIO-6864: one-off, no order behind it, session
 * optional. The amounts and the free field are the mockup PF signed off; the rest of the form
 * is whatever the chosen path needs.
 *
 * Three paths, and which one is on decides what gets sent:
 *   - signed in with a card on file  -> `{ amount }`, nothing else, no captcha;
 *   - signed in paying with another card -> amount + opaqueData + captcha;
 *   - guest -> the above plus who they are, since no account says it for them.
 */
@Component({
  selector: 'app-voluntary-donation',
  imports: [CommonModule, ReactiveFormsModule, TurnstileComponent],
  templateUrl: './voluntary-donation.component.html',
  styleUrl: './voluntary-donation.component.scss'
})
export class VoluntaryDonationComponent {

  #activeModal = inject(NgbActiveModal);
  #donationService = inject(DonationService);
  #localStorageService = inject(LocalStorageService);
  #authorizeService = inject(AuthorizeService);
  #fb = inject(FormBuilder);

  // `viewChild` rejects an ES-private field, so this one is TypeScript-private instead.
  private turnstileRef = viewChild(TurnstileComponent);

  readonly presetAmounts = [5, 10, 25, 50, 100];

  /** Cloudflare caps the label at `[a-zA-Z0-9_-]{1,32}`; its own so donations read separately. */
  readonly turnstileAction = 'donation_checkout';
  readonly captchaEnabled = TurnstileComponent.enabled;

  /**
   * Read from storage, not from the session signal: `ApiService` decides whether to attach the
   * Bearer from exactly this value, and the two have to agree. If the modal thought it had a
   * guest while the request went out authenticated, it would be asking for a name and email
   * the backend then ignores.
   */
  #session: Session | null = this.#localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;

  hasSession = signal(!!this.#session?.accountInfo?.id);
  savedCardNumber = signal(this.#session?.creditCard?.number ?? null);
  hasSavedCard = computed(() => !!this.savedCardNumber());

  /** Off only while a signed-in donor is paying with the card already on file. */
  useNewCard: WritableSignal<boolean> = signal(false);

  amount: WritableSignal<number | null> = signal(null);
  displayAmount: WritableSignal<string> = signal('');

  isSubmitting = signal(false);
  errorText: WritableSignal<string | null> = signal(null);
  /** Set once the donation is taken; the form is replaced by the closing message. */
  doneText: WritableSignal<string | null> = signal(null);

  #turnstileToken: string | null = null;

  form = this.#fb.group({
    email: [''],
    firstName: [''],
    lastName: [''],
    cardNumber: [''],
    exp: [''],
    cvc: [''],
    zip: ['']
  });

  /** A card is tokenised unless a signed-in donor is paying with the one on file. */
  needsCard = computed(() => !this.hasSession() || this.useNewCard() || !this.hasSavedCard());

  /** Set while the captcha has not produced a token yet, so the form can say why it waits. */
  awaitingCaptcha = signal(false);

  /**
   * Set when the wait has gone on long enough that it is not a wait any more.
   *
   * Turnstile reports a failure by handing back a null token, which is the same thing it says
   * while it is still thinking — so there is nothing to distinguish "checking" from "this is
   * never going to finish". A widget blocked by a privacy extension, or served to a hostname
   * its site key does not cover, leaves the donor looking at "verifying" and a dead button
   * with nothing to do about it. After fifteen seconds we stop claiming to be verifying.
   */
  captchaStalled = signal(false);
  #captchaTimer: ReturnType<typeof setTimeout> | null = null;

  canSubmit = computed(() =>
    !this.isSubmitting()
    && !this.doneText()
    && !!this.amount()
    && (!this.needsCard() || !this.captchaEnabled || !this.awaitingCaptcha())
  );

  constructor() {
    // The script is fetched while the donor fills the form in, not when they press the button.
    this.#authorizeService.preload();
    this.useNewCard.set(false);
    // A guest always sends a card, so the wait starts with the form.
    this.awaitingCaptcha.set(this.captchaEnabled && this.needsCard());
    if (this.awaitingCaptcha()) this.#startCaptchaTimer();
    this.#applyValidators();
  }

  //#region Amount

  selectPreset(value: number) {
    this.amount.set(value);
    this.displayAmount.set(`$${value.toFixed(2)}`);
    this.errorText.set(null);
  }

  onAmountInput(event: Event) {
    const raw = (event.target as HTMLInputElement).value.replace(/[^\d.]/g, '');
    const [whole, ...rest] = raw.split('.');
    const cents = rest.length ? '.' + rest.join('').slice(0, 2) : '';
    const value = whole + cents;

    this.displayAmount.set(value ? `$${value}` : '');
    this.amount.set(value ? parseFloat(value) : null);
    this.errorText.set(null);
  }

  isPresetSelected(value: number) {
    return this.amount() === value;
  }

  //#endregion

  //#region Card choice

  payWithSavedCard() {
    this.useNewCard.set(false);
    this.#applyValidators();
  }

  payWithNewCard() {
    this.useNewCard.set(true);
    this.awaitingCaptcha.set(this.captchaEnabled);
    if (this.captchaEnabled) this.#startCaptchaTimer();
    this.#applyValidators();
  }

  /**
   * Only the fields the chosen path actually sends are required. Re-run on every switch so a
   * donor who opens the card form and then goes back to the saved one is not blocked by
   * validators on fields that are no longer in play.
   */
  #applyValidators() {
    const card = this.needsCard();
    const guest = !this.hasSession();

    const rules: Record<string, any[]> = {
      email: guest ? [Validators.required, Validators.email] : [],
      firstName: guest ? [Validators.required] : [],
      lastName: guest ? [Validators.required] : [],
      cardNumber: card ? [Validators.required] : [],
      exp: card ? [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/\d{4}$/)] : [],
      cvc: card ? [Validators.required] : [],
      // Optional for the backend, required here: Authorize.Net checks it against the card and
      // declines without it, and a decline costs the donation.
      zip: card ? [Validators.required] : []
    };

    for (const [name, validators] of Object.entries(rules)) {
      const control = this.form.get(name);
      control?.setValidators(validators);
      control?.updateValueAndValidity({ emitEvent: false });
    }
  }

  invalid(name: string) {
    const control = this.form.get(name);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  //#endregion

  //#region Captcha

  onTurnstileToken(token: string | null) {
    this.#turnstileToken = token;
    this.awaitingCaptcha.set(!token);
    if (token) {
      this.captchaStalled.set(false);
      this.#clearCaptchaTimer();
    } else {
      this.#startCaptchaTimer();
    }
  }

  #startCaptchaTimer() {
    this.#clearCaptchaTimer();
    this.#captchaTimer = setTimeout(() => this.captchaStalled.set(true), 15000);
  }

  #clearCaptchaTimer() {
    if (this.#captchaTimer) clearTimeout(this.#captchaTimer);
    this.#captchaTimer = null;
  }

  /** One token per submission, whatever came back. */
  #resetCaptcha() {
    this.#turnstileToken = null;
    this.captchaStalled.set(false);
    const waiting = this.captchaEnabled && this.needsCard();
    this.awaitingCaptcha.set(waiting);
    if (waiting) this.#startCaptchaTimer();
    this.turnstileRef()?.reset();
  }

  //#endregion

  //#region Submit

  donate() {
    if (this.isSubmitting() || this.doneText()) return;

    this.errorText.set(null);

    if (!this.amount()) {
      this.errorText.set('Please choose or enter an amount.');
      return;
    }

    if (this.needsCard()) {
      this.form.markAllAsTouched();
      if (this.form.invalid) return;
      this.#tokenizeThenCharge();
      return;
    }

    this.#charge();
  }

  /** The card never reaches us: Accept.js swaps it for a one-use token in the browser. */
  #tokenizeThenCharge() {
    const value = this.form.value;
    const [month, year] = (value.exp || '').split('/');

    const card: ANetPayload = {
      cardNumber: (value.cardNumber || '').replace(/\s/g, ''),
      month: month || '',
      year: year || '',
      cardCode: value.cvc || ''
    };

    this.isSubmitting.set(true);
    this.#authorizeService.sendPaymentDataToAnet(
      card,
      (opaque: ANetResponse) => this.#charge({ dataDescriptor: opaque.dataDescriptor, dataValue: opaque.dataValue }),
      // Tokenising can fail without ever reaching the handler above — a declined format, the
      // script blocked, the page not on HTTPS. AuthorizeService says what happened in a toast;
      // what it cannot do is give this form its button back, so that is done here.
      () => {
        this.isSubmitting.set(false);
        this.#resetCaptcha();
      }
    );
  }

  #charge(opaqueData?: DonationOpaqueData) {
    this.isSubmitting.set(true);

    const payload: DonationCheckoutRequest = { amount: this.amount() as number };

    if (opaqueData) {
      payload.opaqueData = opaqueData;
      if (this.#turnstileToken) payload.turnstileToken = this.#turnstileToken;
    }

    if (!this.hasSession()) {
      const value = this.form.value;
      payload.donor = {
        email: (value.email || '').trim(),
        firstName: (value.firstName || '').trim(),
        lastName: (value.lastName || '').trim()
      };
    }

    if (opaqueData) {
      const billing: DonationBillingAddress = { zip: (this.form.value.zip || '').trim() };
      payload.billingAddress = billing;
    }

    this.#donationService.checkout(payload).subscribe(result => {
      this.isSubmitting.set(false);
      this.#resetCaptcha();

      if (result.ok) {
        this.doneText.set(
          result.held
            // Not an error and not a retry: Authorize.Net resolves the review later and the
            // backend polls it, so the donor hears the outcome by email either way — approved,
            // or "we couldn't process your donation" with nothing charged. Asking them to try
            // again here would take a second donation from the ones that do go through.
            ? "Thank you! Your donation is being reviewed by our payment processor. You'll receive an email once it's approved."
            : result.message
        );
        return;
      }

      this.#handleRejection(result.code, result.message, result.retryAfter);
    });
  }

  #handleRejection(code: DonationErrorCode | null, message: string, retryAfter: number | null) {
    switch (code) {
      case 'payment_required':
        // Either a guest sent no card or the account has none on file. Same answer: ask for one.
        this.useNewCard.set(true);
        this.#applyValidators();
        this.errorText.set('Please enter a card to complete your donation.');
        return;

      case 'captcha_required':
      case 'captcha_invalid':
        this.errorText.set('Please complete the verification and try again.');
        return;

      case 'card_declined':
        this.errorText.set('That card was declined. Please try another one.');
        return;

      case 'customer_not_found':
        // The token points at nobody. Carrying on as a guest is the only path that can work.
        this.useNewCard.set(true);
        this.#applyValidators();
        this.errorText.set('Please enter your details to complete your donation.');
        return;

      case 'in_process': {
        // Only reachable on a double submit, which the disabled button already prevents — so
        // in practice this means the first request was lost in transit. The wait is short and
        // the backend sends it, rather than us guessing.
        const seconds = retryAfter && retryAfter > 0 ? retryAfter : null;
        this.errorText.set(seconds
          ? `Your donation is already being processed. Please wait ${seconds} seconds and try again.`
          : 'Your donation is already being processed. Please wait a moment before trying again.');
        return;
      }

      case 'attempt_limit': {
        const minutes = retryAfter ? Math.ceil(retryAfter / 60) : null;
        this.errorText.set(minutes
          ? `Too many attempts. Please try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`
          : 'Too many attempts. Please try again later.');
        return;
      }

      case 'card_error':
        this.errorText.set('We could not reach the payment service. Please try again in a few minutes.');
        return;

      case 'unavailable':
        this.errorText.set('Donations are not available right now. Please try again later.');
        return;

      // invalid_amount and invalid_donor carry a message written to be shown; so does anything
      // the backend adds later that we have not got a case for.
      default:
        this.errorText.set(message);
    }
  }

  //#endregion

  close() {
    this.#clearCaptchaTimer();
    this.#activeModal.close({ donated: !!this.doneText() });
  }
}
